import React, { useState, useEffect, useRef } from "react";
import "./WhatWeDo.css";

const capabilities = [
  {
    number: "01",
    title: "Offshore/Swamp Oil and Gas Facilities Construction and Installation Services",
    image: "/images/services2.webp",
    description:
      "We deliver end-to-end infrastructure and operational support for energy assets across offshore, swamp and onshore environments, helping clients improve reliability, safety and performance.",
    details:
      "This service covers the construction, installation and integration of offshore and swamp facilities, including structural works, mechanical installation and commissioning support tailored to complex energy environments.",
  },
  {
    number: "02",
    title: "Offshore/Swamp Oil and Gas Pipeline Construction and Installation",
    image: "/images/hookup and comissioning.webp",
    description:
      "From planning through execution, we handle pipeline installation, rehabilitation and repair works with a strong emphasis on quality, environmental protection and long-term asset integrity.",
    details:
      "We plan, install and maintain critical pipeline networks that connect production facilities, reduce operational disruption and support the long-term reliability of upstream and midstream assets.",
  },
  {
    number: "03",
    title: "Integrated Pipeline Repair Services",
    image: "/images/iprs.webp",
    description:
      "Our integrated pipeline repair solutions help protect critical infrastructure, reduce downtime and keep energy operations safe, reliable and moving forward.",
    details:
      "Integrated Pipeline Repair Services provide coordinated repair solutions that help restore pipeline performance, protect critical infrastructure and support safe, reliable operations.",
  },
  {
    number: "04",
    title: "Diving and Subsea Inspection, Maintenance & Repair Services (IMR)",
    image: "/images/diving and subsea installation.webp",
    description:
      "Our subsea and diving teams provide inspection, maintenance and repair services that help protect underwater assets and keep offshore operations safe and efficient.",
    details:
      "Our IMR support includes underwater inspection, asset integrity checks, repair interventions and preventative maintenance to help clients reduce downtime and maintain safe offshore performance.",
  },
  {
    number: "05",
    title: "Petroleum terminal tank construction, rehabilitation, repair & maintenance",
    image: "/images/Tank Farm Construction and Reh Services.webp",
    description:
      "Strides Energy and Maritime Limited provides specialized service in above ground petroleum storage construction, rehabilitation, repairs and maintenance.",
    details:
      "Strides Energy and Maritime Limited provides specialized service in above-ground petroleum storage construction, rehabilitation, repairs and maintenance.\n\nOur rehabilitation, repairs and maintenance services follow API 653 standards.\n\nRange of services includes:\n1. Terminal piping works\n2. Tank bottom replacements\n3. Foundation replacements\n4. Cone roof, dome and structure replacements\n5. Internal and external floating roofs\n6. Floating roof seal repair and replacements\n7. Shell nozzles and appurtenances\n8. Rolling ladder repairs and self-leveling steps\n9. Secondary containment tank bottoms and systems\n10. Stairways, handrails and platforms\n11. Tank sucking and re-leveling\n12. Fire prevention and foaming systems.",
  },
  {
    number: "06",
    title: "Subsea Umbilicals & Risers Fabrication & Installation",
    image: "/images/vessel suppy operations.webp",
    description:
      "We provide reliable vessel supply and maritime support services that keep field activities moving with efficiency, coordination and operational discipline.",
    details:
      "This capability focuses on the fabrication and installation of subsea control systems and flowline infrastructure, ensuring safe, efficient connection between offshore facilities and production systems.",
  },
  {
    number: "07",
    title: "Vessels Supply & Operation",
    image: "/images/photos/vessels/tiger shark/Tiger Shark 1.webp",
    description:
      "Our dredging and marine construction capabilities support safe waterways, efficient operations and infrastructure readiness in complex coastal and offshore settings.",
    details:
      "We deliver marine logistics support through dependable vessel supply and operational coordination, enabling clients to maintain continuity in offshore and coastal field operations.",
  },
  {
    number: "08",
    title: "Shallow Offshore Hook-up & Commissioning",
    image: "/images/hookup and comissioning.webp",
    description:
      "Our dredging and marine construction capabilities support safe waterways, efficient operations and infrastructure readiness in complex coastal and offshore settings.",
    details:
      "This work includes mechanical hookup, testing, pre-commissioning and startup support for shallow offshore facilities to ensure systems are ready for reliable production and safe operation.",
  },
  {
    number: "09",
    title: "Dredging Operations",
    image: "/images/dredging.webp",
    description:
      "Our dredging and marine construction capabilities support safe waterways, efficient operations and infrastructure readiness in complex coastal and offshore settings.",
    details:
      "We carry out dredging and marine earthworks that improve navigation, prepare work areas and support coastal and offshore projects in demanding environments.",
  },
];

function WhatWeDo() {
  const [activeCapability, setActiveCapability] = useState(null);
  const triggerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!activeCapability) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveCapability(null);
        return;
      }

      if (event.key !== "Tab") return;

      const focusableElements = dialogRef.current?.querySelectorAll(
        'button, a[href]'
      );
      if (!focusableElements?.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      triggerRef.current?.focus();
    };
  }, [activeCapability]);

  return (
    <main className="what-we-do-page">
      <section
        className="what-we-do-hero"
        style={{
          backgroundImage:
            "linear-gradient(120deg, rgba(14, 17, 20, 0.74), rgba(14, 17, 20, 0.4)), url('/images/cluster 5a.webp')",
        }}
      >
        <div className="what-we-do-hero-overlay" />
        <div className="what-we-do-container what-we-do-hero-content">
          <span className="what-we-do-label">What We Do</span>
          <h1>
            Integrated solutions for
            <br />
            <span>complex energy and maritime operations.</span>
          </h1>
          <p>
            Strides Energy & Maritime delivers practical, performance-driven
            services across infrastructure, offshore operations, marine logistics
            and industrial asset support.
          </p>
        </div>
      </section>

      <section className="what-we-do-intro section-light">
        <div className="what-we-do-container">
          <div className="section-heading">
            <span className="what-we-do-label">Core services</span>
            <h2>
              Trusted capability across
              <br />
              <span>critical sectors.</span>
            </h2>
          </div>

          <div className="what-we-do-grid">
            {capabilities.map((item) => (
              <article className="what-we-do-card" key={item.number}>
                  <div className="what-we-do-card-images">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="what-we-do-card-image"
                    />
                  </div>

                  <div className="what-we-do-card-top">
                    <span className="what-we-do-number">{item.number}</span>

                    <button
                      type="button"
                      className="what-we-do-arrow"
                      onClick={(event) => {
                        triggerRef.current = event.currentTarget;
                        setActiveCapability(item);
                      }}
                      aria-haspopup="dialog"
                      aria-label={`More about ${item.title}`}
                    >
                      →
                    </button>
                  </div>

                  <div className="what-we-do-card-content">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>

                  <div className="what-we-do-card-line" />
              </article>
            ))}
          </div>
        </div>
      </section>

      {activeCapability && (
        <div
          className="what-we-do-card-overlay is-visible"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setActiveCapability(null);
            }
          }}
        >
          <section
            ref={dialogRef}
            className="what-we-do-card-overlay-inner"
            role="dialog"
            aria-modal="true"
            aria-labelledby="what-we-do-overlay-title"
          >
            <button
              ref={closeButtonRef}
              type="button"
              className="what-we-do-close-button"
              onClick={() => setActiveCapability(null)}
              aria-label="Close service overview"
            >
              ×
            </button>

            <span className="what-we-do-overlay-label">Service overview</span>
            <h2 id="what-we-do-overlay-title">{activeCapability.title}</h2>
            <p>{activeCapability.details}</p>
          </section>
        </div>
      )}

      <section className="what-we-do-principles">
        <div className="what-we-do-container">
          <div className="section-heading centered">
            <span className="what-we-do-label">How we work</span>
            <h2>
              Built on <span>discipline, safety and execution.</span>
            </h2>
          </div>

          <div className="what-we-do-principles-grid">
            <div className="principle-card">
              <span className="principle-number">01</span>
              <h3>Technical Excellence</h3>
              <p>
                We combine engineering know-how with field experience to deliver
                reliable, high-quality solutions on time and within specification.
              </p>
            </div>

            <div className="principle-card dark-card">
              <span className="principle-number">02</span>
              <h3>Safe Delivery</h3>
              <p>
                Safety remains central to all operations, from project planning to
                execution, site supervision and post-project review.
              </p>
            </div>

            <div className="principle-card">
              <span className="principle-number">03</span>
              <h3>Client-Centered Execution</h3>
              <p>
                We work closely with clients to understand site realities, solve
                operational challenges and support sustained project value.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default WhatWeDo;
